import type { Translation } from './tr.ts';

const en: Translation = {
  meta: {
    tagline: 'Get your video ready to share',
    /** Search result title: what people search for first, the name last (50–60 characters). */
    title: 'Blur faces, cut silences, add captions, no upload | {{name}}',
    needsJs: 'Shycut runs in your browser and needs JavaScript. Turn it on to edit your video.',
    otherLanguage: 'Türkçe',
    notFound: 'This page doesn’t exist.',
    backHome: 'Go to the video tool',
    description:
      'Free, private video tool: blur faces and licence plates, cut silences, add captions and resize for Reels, TikTok and Shorts — all in your browser.',
  },
  common: {
    cancel: 'Cancel',
    close: 'Close',
    retry: 'Try again',
    done: 'Done',
    on: 'On',
    off: 'Off',
    seconds: '{{value}} s',
    of: '{{current}} / {{total}}',
    downloadFailed: 'Part of the app couldn’t be downloaded. Check your connection and try again.',
  },
  nav: {
    language: 'Language',
    theme: 'Theme',
    themeLight: 'Light theme',
    themeDark: 'Dark theme',
    themeSystem: 'System theme',
    moreProjects: 'More projects',
    install: 'Install app',
    home: '{{name}} home',
  },
  update: {
    ready: 'A new version is ready.',
    apply: 'Update',
    later: 'Later',
    version: 'Version {{build}}',
    check: 'Check for updates',
    checking: 'Checking…',
    current: 'Up to date',
    offline: 'Offline',
    updated: 'Shycut was updated',
  },
  home: {
    /** The page's H1: the words people search with (the display line is `title`). */
    heading: 'Free, private video editor that runs in your browser',
    title: 'What are you making today?',
    subtitle: 'Pick what the video is for. Hide faces and plates, cut the dead air and fit the format, all on your device. Nothing is uploaded.',
    choose: 'Choose file',
    dropHere: 'Drop your video here',
    dropActive: 'Drop it, we’ll take it from here',
    dropHint: 'MP4, MOV, WebM or MKV. Pick several to join them end to end.',
    dropTrust: '{{size}} uploaded. Your video stays here.',
    convert: {
      display: 'Convert video without uploading it',
      subtitle: 'Your browser does the work, on your device. Your video isn’t sent anywhere: no sign-up, no watermark.',
      title: 'Convert to {{format}}',
      body: 'Drop a video and it opens ready to save as {{format}}, whole and as it is. Drop several and they’re converted one after another.',
      formats: 'Convert to',
      hint: 'MP4, MOV, WebM or MKV. Several at once are converted one by one.',
      edit: 'Edit the video instead',
      compressDisplay: 'Compress video without uploading it',
      titleLimited: 'Compress to under {{limit}} MB',
      bodyLimited: 'Drop a video and it opens ready to save as {{format}} under {{limit}} MB: Shycut picks the quality that fits. Drop several and each stays under it.',
      limit: 'Under {{limit}} MB',
      dropLimit: 'Remove the size limit',
    },
    captions: {
      display: 'Captions written on your device',
      subtitle: 'Speech to timed captions and SRT files, in your browser. Nothing is uploaded and there is no sign-up.',
      title: 'Write captions in {{language}}',
      titleTranslate: 'Write English captions',
      titleTranslateFrom: 'Turn {{language}} speech into English captions',
      body: 'Drop a video and it opens on the Captions tab, set up and ready to write. The speech model downloads once and runs here.',
      hint: 'MP4, MOV, WebM or MKV, with speech in it.',
      edit: 'Choose a recipe instead',
    },
    captionLink: {
      language: 'Captions will be written in {{language}}',
      translate: 'Captions will be translated into English',
      translateFrom: 'Captions: {{language}} speech, written in English',
      drop: 'Don’t set up the captions',
    },
    fit: {
      display: 'Fit your video for where it’s going',
      subtitle: 'The shape, length and file size of the place you post to, set on your own device. Nothing is uploaded.',
      title: 'Fit a video for {{place}}',
      body: 'Drop a video and it opens reframed to {{shape}}, its length checked against {{place}} as you trim.',
      bodyOriginal: 'Drop a video and it opens in its own shape, which {{place}} takes, with the length checked as you trim.',
      places: 'Fit for',
      hint: 'MP4, MOV, WebM or MKV. Several at once open together, ready to join.',
      withRecipe: 'With the “{{recipe}}” recipe: {{promise}}',
      edit: 'Edit without fitting',
    },
    inspect: {
      display: 'See what your video gives away',
      subtitle: 'Phones and cameras write where, when and on what a video was made into the file itself. Shycut reads it on your device and sends nothing.',
      title: 'Check a video’s hidden details',
      body: 'Drop a video to see the place, the device and the time it carries, then save a clean copy without them.',
      hint: 'MP4, MOV, WebM or MKV. Only the file’s details are read, never its picture.',
      reading: 'Reading the file’s details…',
      reportsLabel: 'What the files say',
      found: 'This video says {{list}}.',
      none: 'Nothing about where, when or on what it was made was found in this video.',
      reveals: {
        location: 'where it was made',
        track: 'the route it was filmed along',
        device: 'which device made it',
        recorded: 'when it was recorded',
      },
      rows: {
        location: 'Location',
        track: 'GPS track',
        device: 'Device',
        recorded: 'Recorded',
        software: 'Software',
      },
      inFile: 'In the file',
      notFound: 'Not in this file',
      position: '{{latitude}}° {{northSouth}}, {{longitude}}° {{eastWest}}',
      hemispheres: { N: 'N', S: 'S', E: 'E', W: 'W' },
      positionNote: 'Shown rounded to about a kilometre; the file holds the exact point.',
      track_one: '{{count}} position, from {{position}}',
      track_other: '{{count}} positions, from {{position}}',
      trackNote: 'A camera’s GPS log: the whole route, second by second.',
      recordedZone: '{{when}} (UTC{{offset}})',
      other_one: '{{count}} more detail',
      other_other: '{{count}} more details',
      clean: 'Save a clean copy as {{format}}',
      cleanNote: 'The copy is written again from the picture and sound alone, so none of these details come along.',
      another: 'Check another video',
      edit: 'Edit the video instead',
    },
    intentsLabel: 'What is the video for?',
    record: {
      title: 'Record your screen and yourself',
      tagline: 'Screen, camera and mic, on this device.',
      cta: 'Record',
      promise: 'Pauses are trimmed and your voice cleaned up after. Your camera stays a layer you can move.',
      close: 'Close the recorder',
    },
    intents: {
      recording: {
        tag: 'Tutorial · Demo · Talk',
        title: 'A screen recording',
        promise: 'Your screen and you, with pauses trimmed and a clean voice.',
        detail: 'Your camera stays a separate layer you can move later. Recorded on this device.',
      },
      reels: {
        tag: 'Reels · TikTok · Shorts',
        title: 'A vertical clip',
        promise: '9:16 that follows your face, silences cut, captions ready.',
        detail: 'Faces and plates hidden. Captions use a one-time model download.',
      },
      youtube: {
        tag: 'YouTube',
        title: 'A YouTube video',
        promise: '16:9 with gentle cuts and balanced sound.',
        detail: 'Phone footage gets a blurred-background fill. High\u2011quality export.',
      },
      privacy: {
        tag: 'Privacy',
        title: 'Hide faces and plates',
        promise: 'A thorough scan. Nothing else changes.',
        detail: 'Faces and licence plates are found and followed as they move.',
      },
      motovlog: {
        tag: 'Ride footage',
        title: 'A motovlog',
        promise: 'Wind removed, plates and passers\u2011by hidden.',
        detail: 'For helmet and chest cams. GoPro GPS becomes a speed gauge.',
      },
      faces: {
        tag: 'Privacy',
        title: 'Hide faces',
        promise: 'Every face found and blurred, followed as it moves.',
        detail: 'Starts as soon as the video opens. A one-time ~4\u00a0MB download.',
      },
      plates: {
        tag: 'Privacy',
        title: 'Hide licence plates',
        promise: 'Plates found and blurred, frame by frame.',
        detail: 'Starts as soon as the video opens. A one-time 8–15\u00a0MB download.',
      },
      fillers: {
        tag: 'Sound',
        title: 'Cut “um”s and “uh”s',
        promise: 'Hesitations cut out. Every word you meant stays.',
        detail: 'Found in the sound itself, on your device. Nothing to download.',
      },
      captions: {
        tag: 'Captions',
        title: 'Add captions',
        promise: 'Word-timed captions, written on your device.',
        detail: 'Starts on its own. The speech model (100–300\u00a0MB) downloads once; on mobile data it waits for you.',
      },
      blank: {
        tag: 'Anything',
        title: 'Just a video',
        promise: 'Opens as it is: nothing scanned or cut until you choose.',
        detail: 'Every tool is a tap away: faces, cuts, captions, size.',
      },
      silence: {
        tag: 'Sound',
        title: 'Cut pauses and “um”s',
        promise: 'Dead air and hesitations cut out. Every word stays.',
        detail: 'Found in the sound itself, on your device. Nothing to download.',
      },
      subtitles: {
        tag: 'Subtitles',
        title: 'Make a subtitle file',
        promise: 'Speech written as timed lines, saved as an SRT file.',
        detail: 'Kept off the picture, for YouTube or any player. The speech model (100–300\u00a0MB) downloads once; on mobile data it waits for you.',
      },
    },
    jobsLabel: 'Or just one job',
    mine: 'Your recipes',
    thumbAlt: 'Example result: {{title}}',
    proofTitle: 'Private by construction',
    proof: {
      device: { title: 'Runs on your device', body: 'Your browser decodes, analyses and encodes the video. There is no server to send it to.' },
      ledger: { title: 'Nothing is uploaded', body: 'The counter in the header measures every byte this page sends. It stays at zero.' },
      location: { title: 'Location data wiped', body: 'Phones hide GPS positions inside videos. The exported file never carries them.' },
    },
    privacyNote: 'Free, no sign-up, no watermark.',
    faq: {
      title: 'Questions',
      items: {
        upload: {
          q: 'Is my video uploaded anywhere?',
          a: 'No. Your browser opens, edits and saves the video on your own device. The only downloads are the AI models, once; the counter at the top shows that nothing is uploaded.',
        },
        faces: {
          q: 'How do I blur faces in a video?',
          a: 'Open the video and tap “Search” next to faces in the Privacy tab. Faces are found and followed automatically and hidden with blur, pixels, an emoji or a box. Tap anyone who should stay visible, like yourself.',
        },
        plates: {
          q: 'Can it hide licence plates and other things?',
          a: 'Yes. Search for plates with one tap, or draw around anything else (a house number, a logo, a screen) and it is followed as it moves.',
        },
        cuts: {
          q: 'Can it cut silences and “um”s?',
          a: 'Yes. Switch cutting on and pauses and held hesitation sounds like “um”, “uh” and “eee” are found and cut. You can also remove any part by hand, or delete words in the Text tab.',
        },
        captions: {
          q: 'Which languages do the captions support?',
          a: 'Speech is turned into captions on your device in Turkish, English, German, French, Spanish and more. The language is detected, and you can choose it yourself.',
        },
        phone: {
          q: 'Does it work on my phone?',
          a: 'Yes, in a current Chrome or Safari, and you can install it like an app. Long videos are faster on a computer.',
        },
        free: {
          q: 'Is it really free?',
          a: 'Yes: no sign-up, no watermark, no limits. Everything runs on your device, so there is no server to pay for.',
        },
      },
    },
    footer: 'Privacy-first, built on open web technology.',
    blog: {
      title: 'From the blog',
      lead: 'Guides and tips for posting videos that stay private.',
      all: 'All articles',
      fallback: 'Articles are in {{language}} for now.',
      loading: 'Loading articles',
    },
  },
  /** Links to the content pages (src/config/site-pages.ts); the build uses the same names on those pages. */
  site: {
    toolsTitle: 'Free tools',
    compareTitle: 'Compare',
    blog: 'Blog',
    links: {
      faces: 'Blur faces in video',
      pixelate: 'Pixelate a face',
      moving: 'Blur a moving face',
      kids: 'Blur children’s faces',
      plates: 'Blur license plates',
      dashcam: 'Dashcam video editor',
      location: 'Remove location data',
      metadata: 'Video metadata viewer',
      findlocation: 'Check a video’s location',
      silence: 'Remove silences',
      captions: 'Add captions to video',
      srt: 'SRT subtitle generator',
      resize: 'Resize for Reels & TikTok',
      merge: 'Merge videos',
      mixed: 'Merge portrait and landscape',
      music: 'Add music to video',
      speedup: 'Speed up video',
      screen: 'Screen recorder',
      motovlog: 'Motovlog editor',
      gopro: 'Merge GoPro videos',
      speed: 'GoPro speed overlay',
      wind: 'Remove wind noise',
      capcut: 'CapCut alternative',
      veed: 'VEED alternative',
      kapwing: 'Kapwing alternative',
      blurme: 'BlurMe alternative',
      descript: 'Descript alternative',
      clipchamp: 'Clipchamp alternative',
      flexclip: 'FlexClip alternative',
      inshot: 'InShot alternative',
      clideo: 'Clideo alternative',
      loom: 'Loom alternative',
      obs: 'OBS alternative',
      screenpal: 'ScreenPal alternative',
      handbrake: 'HandBrake alternative',
      cloudconvert: 'CloudConvert alternative',
      freeconvert: 'FreeConvert alternative',
      privacy: 'Privacy policy',
    },
    /** Hubs of the programmatic pages (HUB_PAGES in src/config/site-pages.ts). */
    hubs: {
      convert: 'Video converter',
      compress: 'Video compressor',
      specs: 'Video specs',
      jobs: 'Platform guides',
      filesize: 'Video file size',
    },
  },
  load: {
    reading: 'Reading video…',
    joining: 'Joining camera chapters… {{percent}}%',
    errors: {
      unsupportedFormat: 'This file type isn’t supported. Try MP4, MOV, WebM or MKV.',
      noVideo: 'No video was found in this file.',
      noAudio: 'No sound this browser can play was found in this file.',
      codecUnsupported: 'Your browser can’t decode this video’s codec ({{codec}}). Try Chrome or Edge, or save the video as MP4 (H.264).',
      browserUnsupported: 'Your browser doesn’t support video processing. Use a recent Chrome, Edge or Safari.',
      unknown: 'Couldn’t open the video: {{message}}',
      sourceUnreadable: 'The video can’t be read anymore. It may have been changed or moved since you opened it; open it again.',
    },
  },
  ledger: {
    chip: '{{size}} uploaded',
    title: 'Privacy ledger',
    uploaded: 'Uploaded',
    downloaded: 'Downloaded this visit',
    thisSite: 'This site',
    nothingYet: 'Nothing downloaded yet.',
    explain: 'Your video and audio never leave this device. This site connects only to itself, to Hugging Face for AI models and, if you agreed, to Google Analytics to count visits. It never sends your files.',
    analytics: 'Visit statistics (Google Analytics)',
    analyticsNote: 'Which pages were visited, never your files. Not counted as an upload.',
    verify: 'Check it yourself: open your browser’s developer tools and watch the Network tab, or switch to airplane mode once the models have downloaded.',
    unexpected: 'An upload was detected. Please report this: it should never happen.',
  },
  editor: {
    newVideo: 'New video',
    leaveTitle: 'Leave this video?',
    leaveBody: 'Your edits, scan results and captions will be discarded. This can’t be undone.',
    leaveRecordingBody: 'This recording hasn’t been exported yet. Leaving deletes it from this device.',
    leaveConfirm: 'Leave',
    undo: 'Undo',
    redo: 'Redo',
    tools: 'Tools',
    export: 'Export',
    tabs: {
      camera: 'Camera',
      privacy: 'Privacy',
      cut: 'Cut',
      text: 'Text',
      format: 'Size',
      audio: 'Audio',
      captions: 'Captions',
      telemetry: 'Ride',
      media: 'Media',
    },
    previewUnsupported: 'This video can’t be previewed in your browser, but exporting works.',
    locationFound: 'This video contains location data — it will be removed on export.',
    clips_one: '{{count}} clip',
    clips_other: '{{count}} clips',
    shortcuts: 'Space: play/pause · ←/→: seek 1 s · X: cut by hand',
  },
  player: {
    play: 'Play',
    timeline: 'Timeline',
    pause: 'Pause',
    editedPreview: 'Result',
    originalPreview: 'Original',
    outputDuration: 'New length {{duration}}',
    space: 'Space',
    modeTooltip: 'Result: with your cuts and everything you added, as it will be exported. Original: the untouched video.',
    lengthTooltip: 'How long the video is after the removed and sped-up parts, and how much shorter that is.',
    saved: '−{{duration}}',
    safeArea: 'Safe area · TikTok, Reels, Shorts',
    safeAreaFor: 'Safe area · {{place}}',
    safeAreaCovered: 'App buttons cover this spot',
  },
  privacy: {
    intro: 'Let AI find and hide the faces and licence plates in your video.',
    layerVideosNotScanned: 'Videos you put on top aren’t scanned, so faces in them stay visible.',
    kinds: { face: 'Faces', plate: 'Licence plates' },
    kindHints: { face: 'Not searched yet', plate: 'Search when vehicles are in shot' },
    kindSuggested: { face: 'Find and hide them with one tap', plate: 'Your recipe hides plates too' },
    kindQueued: 'Waiting…',
    kindFailed: 'The scan couldn’t finish',
    kindFound_one: '{{count}} found',
    kindFound_other: '{{count}} found',
    kindNone: 'None found',
    findKind: 'Search',
    cancelKind: 'Stop searching for {{kind}}',
    rescanKind: 'Search for {{kind}} again',
    clearKind: 'Remove {{kind}} results (nothing gets hidden)',
    precision: 'Scan',
    precisionFast: 'Fast',
    precisionThorough: 'Thorough',
    precisionHint: 'A thorough scan also catches small faces in the distance, but takes longer.',
    scopeNote: 'Only the parts left in your video are scanned. Bring a cut part back and it’s scanned too, by itself.',
    modelNote: 'The AI runs on your device. Models download once, on the first scan (~{{face}} for faces, ~{{plate}} more for plates), and stay on your device for a year or until a newer one is needed.',
    scanning: 'Scanning… {{percent}}%',
    scanningRestored: 'Scanning the parts you brought back… {{percent}}%',
    loadingModel: 'Preparing AI…',
    foundNone: 'Nothing to hide was found.',
    foundNoneHint: 'If a helmet or glasses cover faces, try a “Thorough” scan.',
    enabled: 'Apply hiding',
    style: 'Style',
    styles: { blur: 'Blur', pixelate: 'Pixelate', emoji: 'Emoji', solid: 'Box' },
    strength: 'Strength',
    padding: 'Margin',
    sticker: {
      add: 'Add your own picture',
      yours: 'Your picture',
      hint: 'Or use your own picture: a character to cover the face, or glasses, a moustache or a hat with a see-through background.',
      placement: 'Placement',
      placements: { cover: 'Whole face', eyes: 'Eyes', mouth: 'Mouth', head: 'Head' },
      coverHint: 'Your picture covers the whole face. Anything see-through in it stays blurred underneath.',
      wornHint: 'The face is still blurred, with your picture on top. It fits best on faces looking at the camera; plates and areas are covered whole.',
      change: 'Change',
      remove: 'Remove',
      errors: {
        'not-image': 'That file isn’t a picture. Try a PNG, WebP or JPG.',
        'too-big': 'That picture is too big. Try one under 25 MB.',
        unreadable: 'That picture couldn’t be opened. Try a PNG, WebP or JPG.',
        empty: 'That picture is completely see-through, so there’s nothing to show.',
      },
    },
    items: 'Found',
    itemsHint: 'Tap anything you don’t want hidden — like your own face.',
    hideAll: 'Hide all',
    showAll: 'Show all',
    notification: 'Notification',
    showNotification: 'Show this notification',
    notificationsHint: 'Notifications that popped up on screen are hidden. Remove one to show it.',
    kind: { face: 'Face', plate: 'Plate', manual: 'Area' },
    hidden: 'Hidden',
    visible: 'Visible',
    toggleItem: '{{kind}} · {{start}}–{{end}}',
    jumpTo: 'Jump to this moment',
    addArea: 'Add area',
    addAreaHint: 'Select anything the AI missed (a helmet, a logo, a house number); it is followed as it moves.',
    drawHint: 'Draw around what you want to hide · Esc to cancel',
    trackingArea: 'Following the area… {{percent}}%',
    removeArea: 'Remove area',
    errors: {
      failed: 'The scan couldn’t finish: {{message}}',
      trackFailed: 'Couldn’t follow the area: {{message}}',
      framesUnreadable: 'This browser couldn’t read the video’s frames (it happens with some phones’ HDR videos). Try the latest Chrome, or record with the camera’s “Most compatible / H.264” setting.',
    },
  },
  cut: {
    removed: 'Removed parts',
    removedHint: 'Parts you cut by hand and takes you threw away while recording. Bring one back if you change your mind.',
    manual: 'Remove a part',
    manualHint: 'Take a part out: go to where it starts and press “Remove part”, then go to where it ends and press “Remove up to here”. The clip isn’t split, only that part goes. Shortcut: X.',
    manualMarking: 'Move to where the part to remove ends (play, or drag on the timeline), then press “Remove up to here”. Esc to cancel.',
    manualStart: 'Remove part',
    manualTooltip: 'Takes a part out of the video: marks where it starts, then you go to where it ends and press again.',
    manualFinish: 'Remove up to here',
    manualFinishTitle: 'Remove from {{start}} to here',
    removedLength: '{{duration}} long',
    restore: 'Bring back',
    restoreAll: 'Bring all back',
    trim: 'Trim start and end',
    trimHint: 'Drag the handles on the timeline or use the playhead.',
    start: 'Start',
    end: 'End',
    setStart: 'Set start here',
    setEnd: 'Set end here',
    resetTrim: 'Reset',
    speech: 'Tighten the speech',
    fillers: 'Cut “um”s and “uh”s',
    fillersHint: 'Finds the sounds you hold while thinking (um, uh, hmm, “eee”) and takes them out. Words are left alone.',
    fillerStats_one: '{{count}} hesitation · {{duration}} shorter',
    fillerStats_other: '{{count}} hesitations · {{duration}} shorter',
    noFillers: 'No hesitations like “um” or “uh” were found.',
    fillerListen: 'Listen in the original',
    strictness: 'How tight?',
    silence: 'Cut silent parts',
    silenceHint: 'Automatically removes the pauses between speech.',
    presets: { gentle: 'Gentle', balanced: 'Balanced', aggressive: 'Tight' },
    analyzing: 'Analysing audio…',
    stats_one: '{{count}} silent part · {{duration}} shorter',
    stats_other: '{{count}} silent parts · {{duration}} shorter',
    noSilence: 'No clear silence to cut.',
    everythingRemoved: 'These settings remove the whole video. Loosen the trim or the silence setting.',
  },
  placement: {
    names: {
      'instagram-reels': 'Instagram Reels',
      'instagram-story': 'Instagram Story',
      tiktok: 'TikTok',
      'youtube-shorts': 'YouTube Shorts',
      youtube: 'YouTube',
      linkedin: 'LinkedIn',
      x: 'X',
      pinterest: 'Pinterest',
      shopify: 'Shopify product video',
    },
    short: {
      'instagram-reels': 'Reels',
      'instagram-story': 'Story',
      tiktok: 'TikTok',
      'youtube-shorts': 'Shorts',
      youtube: 'YouTube',
      linkedin: 'LinkedIn',
      x: 'X',
      pinterest: 'Pinterest',
      shopify: 'Shopify',
    },
    units: { h: 'h', min: 'min', s: 's' },
    asFilmed: 'As filmed',
    upTo: 'Up to {{length}}',
    from: 'From {{length}}',
    under: 'Under {{size}}',
    title: 'Fitted for <name>{{place}}</name>',
    length: {
      fits: 'At {{length}}, this edit is a length {{place}} takes.',
      short: '{{place}} takes videos from {{min}}; this edit is {{length}}. Add a little more.',
      long: '{{place}} takes up to {{max}}; this edit is {{length}}. Trim {{over}} to fit.',
      verified: 'Over {{max}}: {{place}} takes it only from verified accounts.',
      premium: 'Over {{max}}: {{place}} takes it only from Premium accounts.',
    },
    reframe: 'Back to {{shape}}',
    reframed: 'The frame was changed since; {{place}} wants {{shape}}.',
    stop: 'Stop fitting',
  },
  format: {
    aspect: 'Aspect ratio',
    aspects: {
      original: { label: 'Original', hint: 'As is' },
      '9:16': { label: '9:16', hint: 'Reels · TikTok · Shorts' },
      '1:1': { label: '1:1', hint: 'Square post' },
      '4:5': { label: '4:5', hint: 'Instagram post' },
      '16:9': { label: '16:9', hint: 'YouTube · landscape' },
    },
    fit: 'Layout',
    fits: { crop: 'Crop', blurFill: 'Blurred background' },
    focus: 'Framing',
    focusHint: 'Drag the frame in the preview to choose the focus point.',
    splitNote: 'In a split frame the whole screen sits beside your camera, so there is nothing to crop.',
    framings: { manual: 'Manual', follow: 'Follow face' },
    followHint: 'The frame smoothly follows the main person. Faces you keep visible come first.',
    followNeedsScan: 'To follow someone, let’s find the faces in the video first.',
    findFaces: 'Find faces',
    findingFaces: 'Finding faces… {{percent}}%',
    noFaces: 'No faces to follow were found in the video.',
    resolution: 'Resolution',
    resolutions: { source: 'Original', '1080': '1080p', '720': '720p' },
    outputSize: 'Output {{width}}×{{height}}',
  },
  audio: {
    mute: 'Mute audio',
    muteHint: 'The video is exported without sound.',
    normalize: 'Balance loudness',
    normalizeHint: 'Brings loudness to the social media standard (-14 LUFS).',
    measured: 'Currently {{lufs}} LUFS',
    lufsExplained: '(LUFS measures how loud sound feels to the ear.)',
    measuring: 'Measuring…',
    noAudio: 'This video has no audio.',
    unsupported: 'Your browser can’t process this video’s audio ({{codec}}); it will be exported without sound. Try a recent Chrome or Safari.',
    denoise: 'Wind and noise reduction',
    denoiseHint: 'Keeps speech, suppresses wind, engine noise and hum. Applied on export.',
    muteKeepsMusic: 'Music and sounds you added still play.',
    music: 'Music and sounds',
    addMusic: 'Add music',
    musicHint: 'Music starts at the beginning and drops by itself while someone speaks. The file never leaves this device.',
  },
  captions: {
    suggested: 'You may be on mobile data, so the captions wait for you. The speech model (~{{size}}) downloads once, when you create them.',
    intro: 'Let’s turn speech into captions automatically. The AI runs on your device; the audio is never sent anywhere.',
    missingClips_one: 'The {{count}} clip added after the captions has none yet.',
    missingClips_other: 'The {{count}} clips added after the captions have none yet.',
    addMissing_one: 'Caption it',
    addMissing_other: 'Caption them',
    language: 'Language',
    languages: {
      auto: 'Detect automatically',
      tr: 'Türkçe',
      en: 'English',
      de: 'Deutsch',
      fr: 'Français',
      es: 'Español',
      it: 'Italiano',
      pt: 'Português',
      ru: 'Русский',
      ar: 'العربية',
      nl: 'Nederlands',
    },
    quality: 'Model',
    qualities: { fast: 'Fast', accurate: 'Accurate' },
    qualityHint: 'The model is downloaded once (Fast ~{{fast}}, Accurate ~{{accurate}}) and stays on your device for a year or until a newer one is needed. “Accurate” is recommended for most languages.',
    generate: 'Create captions',
    regenerate: 'Create again',
    downloading: 'Downloading model… {{percent}}%',
    preparing: 'Preparing AI…',
    downloadSize: '{{loaded}} / {{total}} MB · one-time download',
    staysLocal: 'Only the model is downloaded. Your video and audio never leave your device.',
    transcribing: 'Transcribing speech… {{percent}}%',
    burnIn: 'Burn captions into the video',
    burnInHint: 'Most social media videos are watched without sound.',
    style: 'Style',
    styles: { pop: 'Pop', classic: 'Classic', box: 'Boxed', minimal: 'Minimal' },
    position: 'Position',
    positions: { top: 'Top', middle: 'Middle', bottom: 'Bottom' },
    positionHint: 'You can also drag the captions on the video to any spot.',
    positionPlaced: 'Placed clear of what {{place}} draws over the video. You can still drag them anywhere.',
    positionDragged: 'Captions stay where you dragged them. Pick a position to snap them there.',
    resetPosition: 'Reset',
    size: 'Text size',
    lines: 'Lines',
    linesHint: 'Tap a line to fix a misheard word.',
    downloadSrt: 'Download SRT',
    srtExplained: 'An SRT is a separate captions file. Upload it to YouTube, Vimeo or a video player and viewers can turn captions on or off; it isn’t burned into the video.',
    languageHint: '“Detect automatically” works it out from the speech. If it gets it wrong, pick the language here.',
    detected: 'The speech was detected as {{language}}. If that’s wrong, pick the language above.',
    writtenIn: 'Captions are written in {{language}}.',
    task: 'Write the captions',
    tasks: { transcribe: 'As spoken', translate: 'In English' },
    taskHint: 'In English, the speech model translates as it listens, whatever language is spoken. Lines follow the speech; the timing of single words is approximate.',
    translatedFrom: 'Captions are an English translation of the {{language}} speech.',
    redo: 'Write them again',
    fastTranslation: 'The Fast model translates far less well than Accurate. For English captions, choose Accurate below.',
    redoTaskHint: 'Write the captions again as chosen above? Corrections you made to the lines are lost.',
    redoInLanguage: 'Create in this language',
    redoHint: 'The current captions are in {{language}}. Create them again in the language you picked? Fixes you made to lines will be lost.',
    removeLine: 'Remove line',
    empty: 'No speech was found in the video.',
    errors: {
      failed: 'Couldn’t create captions: {{message}}',
    },
  },
  telemetry: {
    enable: 'Draw the ride gauge',
    enableHint: 'Speed and altitude from your camera’s GPS, drawn into the video.',
    style: 'Look',
    styles: { dial: 'Dial', minimal: 'Minimal' },
    show: 'Show',
    fields: { speed: 'Speed', altitude: 'Altitude', lean: 'Lean angle', gforce: 'G-force' },
    estimatedHint: 'Lean and g-force are worked out from GPS, so treat them as estimates.',
    nothingShown: 'Pick at least one value to show.',
    position: 'Position',
    corners: { 'top-left': 'Top left', 'top-right': 'Top right', 'bottom-left': 'Bottom left', 'bottom-right': 'Bottom right' },
    size: 'Size',
    units: 'Units',
    unitSystems: { metric: 'km/h · m', imperial: 'mph · ft' },
    stats: { topSpeed: 'Top speed', distance: 'Distance', climb: 'Climb', maxLean: 'Max lean' },
    gaps: 'The GPS signal dropped for about {{percent}}% of this video; the gauge shows dashes there.',
    privacyNote: 'Only these values are drawn. Your route and coordinates never appear in the video, and location data is still wiped.',
    noFixTitle: 'No GPS signal in this video',
    noFixBody: 'The camera didn’t record a position. On a GoPro, turn GPS on and wait for the GPS icon to settle before you press record.',
    noneBody: 'This video carries no ride data from the camera.',
  },
  zones: {
    title: 'Private places',
    suggest: 'Rides often start at home. Hide {{radius}} around where this ride starts and ends?',
    suggestAction: 'Hide start and end',
    mapLabel: 'Ride route; hidden parts are dashed',
    mapHint: 'Touch the route to jump the video there. No map is downloaded; the line comes from your camera’s GPS.',
    enable: 'Keep private places out of the video',
    enableHint: 'Time spent near these places is hidden in the video, and the gauge switches off there.',
    hideEnds: 'Hide start and end',
    hideEndsHint: '{{radius}} around where the ride starts and ends.',
    noPlaces: 'No saved places. Pause where you pass your home or work, then add it below.',
    placeName: 'Place {{number}}',
    onRide: 'On this ride',
    notOnRide: 'Not on this ride',
    removePlace: 'Remove place {{number}}',
    addHere: 'Hide around here',
    addHereHint: 'Saves where you are at this moment of the video; it stays hidden in this ride and later ones.',
    noFixHere: 'No GPS signal at this moment; move to a moment that has one.',
    alreadyPrivate: 'This spot is already inside a private place.',
    action: 'What to do',
    actions: { blur: 'Blur', cut: 'Cut' },
    radius: 'Radius',
    hiddenTimes: 'Hidden',
    storedLocally: 'Saved places stay on this device. They are never sent anywhere, or written into recipes or videos.',
  },
  presets: {
    title: 'Recipes',
    hint: 'A recipe stores size, cutting, audio, privacy, caption and ride gauge settings. Video-specific trims and selections aren’t stored.',
    builtIn: {
      recording: { name: 'Screen recording', body: 'Gentle cutting, clean voice, sharp text, faces on screen hidden' },
      reels: { name: 'Reels · TikTok', body: '9:16, face tracking, silence cutting, captions and privacy' },
      youtube: { name: 'YouTube', body: '16:9, blurred background, gentle cutting, high quality' },
      motovlog: { name: 'Motovlog', body: 'Wind reduction, thorough face and plate hiding, speed gauge' },
      privacy: { name: 'Privacy only', body: 'Thoroughly finds and hides faces and plates' },
      faces: { name: 'Hide faces', body: 'Thoroughly finds and hides faces' },
      plates: { name: 'Hide plates', body: 'Thoroughly finds and hides licence plates' },
      fillers: { name: 'Cut “um”s and “uh”s', body: 'Cuts hesitations, nothing else' },
      captions: { name: 'Captions', body: 'Writes word-timed captions on your device' },
      blank: { name: 'Blank', body: 'Nothing applied: original size, no cutting' },
      silence: { name: 'Cut pauses and “um”s', body: 'Cuts dead air and hesitations, nothing else' },
      subtitles: { name: 'Subtitle file', body: 'Writes captions for an SRT file, kept off the picture' },
    },
    mine: 'My recipes',
    empty: 'You haven’t saved a recipe yet.',
    apply: 'Apply',
    saveCurrent: 'Save these settings',
    namePlaceholder: 'Recipe name',
    save: 'Save',
    delete: 'Delete recipe',
  },
  batch: {
    title: 'Batch processing',
    leaveTitle: 'Leave batch processing?',
    leaveBody: 'The list will be cleared. Videos you haven’t downloaded will be lost.',
    subtitle_one: '{{count}} video will be processed with your recipe.',
    subtitle_other: '{{count}} videos will be processed one by one with your recipe.',
    convertSubtitle_one: '{{count}} video will be saved as {{format}}, as it is.',
    convertSubtitle_other: '{{count}} videos will be saved as {{format}} one by one, as they are.',
    convertHint: 'Nothing is cut, reframed or made louder; places you marked as private stay hidden.',
    useRecipe: 'Use a recipe instead',
    justConvert: 'Just convert, no recipe',
    recipe: 'Recipe',
    videos: 'Videos',
    start: 'Process all',
    stop: 'Stop',
    addMore: 'Add videos',
    clear: 'Clear list',
    downloadAll: 'Download all',
    saveToFolder: 'Save all to a folder',
    summary: '{{done}}/{{total}} ready',
    remove: 'Remove from list',
    status: { queued: 'Queued', running: 'Processing', done: 'Ready', error: 'Error', cancelled: 'Stopped' },
    steps: { probe: 'Reading', audio: 'Analysing audio', scan: 'Privacy scan', screen: 'Finding zoom moments', captions: 'Captions', export: 'Saving' },
  },
  export: {
    title: 'Export',
    quality: 'Quality',
    qualities: { small: 'Small file', balanced: 'Balanced', high: 'High' },
    summary: {
      size: 'Size',
      duration: 'Length',
      format: 'Format',
      estimate: 'Estimated file',
    },
    checklist: {
      faces_one: '{{count}} item will be hidden',
      faces_other: '{{count}} items will be hidden',
      noMasks: 'Nothing to hide',
      notSearched: 'Faces and plates weren’t looked for, so nothing will be hidden',
      unscanned: 'Some parts you brought back weren’t scanned, so faces there stay visible',
      location: 'Location data will be removed',
      metadata: 'Device and location metadata will be wiped',
      loudness: 'Audio balanced to -14 LUFS',
      silence: 'Silent parts will be cut',
      muted: 'Audio will be removed',
      denoise: 'Wind and noise will be reduced',
      captions: 'Captions will be burned in',
      telemetry: 'Ride gauge will be drawn in, without your route',
      placesBlurred: 'Time near your private places will be blurred',
      placesCut: 'Time near your private places will be cut',
      audioUnsupported: 'Audio can’t be processed in this browser; the video will be silent',
      joined_one: '{{count}} clip will be joined',
      joined_other: '{{count}} clips will be joined end to end',
      layers_one: '{{count}} layer will be put on top',
      layers_other: '{{count}} layers will be put on top',
      layerFaces: 'Faces in videos on top aren’t hidden',
      speeds_one: '{{count}} part will be sped up',
      speeds_other: '{{count}} parts will be sped up',
      transition: 'Clips will dip softly into each other',
      gifSilent: 'GIFs have no sound; it loops on its own',
      audioUnsupportedAlone: 'Audio can’t be processed in this browser',
    },
    saveAs: 'Save as',
    fileName: 'File name',
    fileType: 'File type',
    whatHappens: 'What happens on the way',
    kinds: {
      video: { label: 'Video', hint: 'To post or send', start: 'Create video', working: 'Creating video', done: 'Your video is ready' },
      gif: { label: 'GIF', hint: 'Loops, no sound', start: 'Create GIF', working: 'Creating GIF', done: 'Your GIF is ready' },
      audio: { label: 'Sound only', hint: 'For podcasts', start: 'Save sound', working: 'Saving sound', done: 'Your sound is ready' },
      image: { label: 'Picture', hint: 'A cover frame', start: 'Save picture', working: 'Saving picture', done: 'Your picture is ready' },
    },
    formats: {
      mp4: 'Plays everywhere: phones, Instagram, TikTok, YouTube.',
      webm: 'Smaller for the same picture; for websites and Android. iPhones may not play it.',
      mov: 'For Apple apps and editing software such as Final Cut and iMovie.',
      gif: 'For chats, docs and pages. Kept small and short: GIFs grow fast.',
      m4a: 'Small and clear; plays on every phone and in podcast apps.',
      mp3: 'Plays in every player and app, old and new.',
      wav: 'Uncompressed, for editing elsewhere. Large files.',
      jpg: 'Small, for covers and thumbnails.',
      png: 'Lossless and sharp; larger.',
    },
    qualityHints: {
      gif: '{{size}} px on the long side · {{fps}} frames a second',
      audio: '{{kbps}} kbps',
    },
    stillAt: 'Saves the frame at {{time}}, where the playhead is. Move it to pick another.',
    limit: {
      title: 'Size limit',
      label: 'Keep it under',
      unit: 'MB',
      none: 'No limit',
      clear: 'Remove the limit',
      hint: 'For mail and chat apps that cap uploads: Shycut picks the quality that fits.',
      fits: 'Comes to about {{size}}, under {{limit}} MB.',
      stepped: 'To fit {{limit}} MB, the picture is made {{width}}×{{height}}: at full size it would look blocky.',
      tooLong: 'This limit holds at most {{length}} of this video. Trim it, or raise the limit.',
      over: 'This came out at {{size}}, over the {{limit}} MB limit. Try a lower limit.',
    },
    problems: {
      gifTooLong: 'GIFs are for short loops. This edit is {{length}}, so the GIF will be its first {{limit}}.',
      noSound: 'This edit has no sound to save. Turn the video’s sound back on, or add music.',
    },
    onDevice: 'On this device',
    receipt: 'Made on your device. Nothing was uploaded.',
    smaller: '{{percent}} smaller than the original',
    alsoAs: 'Also save as',
    announceDone: 'Ready to save',
    start: 'Create video',
    extrasTitle: 'Also save',
    extrasHint: 'Made from the same edit, one after the other: only the frame shape changes. Fit or crop follows the Size tab.',
    extras: {
      vertical: { label: 'Vertical 9:16', hint: 'Reels, TikTok, Shorts' },
      wide: { label: 'Wide 16:9', hint: 'YouTube' },
      square: { label: 'Square 1:1', hint: 'Feed posts' },
      cover: { label: 'Cover picture', hint: 'The frame on screen' },
    },
    mainFile: 'Main file',
    startMany_one: 'Create {{count}} file',
    startMany_other: 'Create {{count}} files',
    batchItem: 'File {{index}} of {{total}} · {{name}}',
    alsoMade: 'Also made',
    downloadAll: 'Download all',
    shareAll: 'Share all',
    downloadOne: 'Download {{name}}',
    partial: 'Not every file could be made: {{message}}',
    stillScanning: 'Faces and plates are still being found. Wait for it to finish; if you create the video now, what hasn’t been found yet stays visible.',
    working: 'Creating… {{percent}}%',
    eta: 'About {{time}} left',
    preparing: 'Preparing…',
    keepOpen: 'Keep this tab open. Everything runs on your device.',
    doneTitle: 'Your video is ready',
    doneBody: '{{size}} · {{duration}}',
    download: 'Download',
    downloadToFiles: 'Download to Files',
    saveToPhotos: 'Save to Photos',
    downloaded: 'Downloaded',
    downloadAgain: 'Download again',
    savedWhere: {
      ios: 'In the Files app, in the Downloads folder.',
      android: 'In your Downloads folder. You can also open it from the notification.',
      desktop: 'In your Downloads folder.',
    },
    openSaved: { ios: 'Open Files', android: 'Open Downloads' },
    downloadSrt: 'Caption file (SRT)',
    share: 'Share',
    again: 'Change settings',
    sourceLost: {
      title: 'The video can’t be read anymore',
      body: 'It may have been changed or moved after you opened it; a gallery or cloud sync can do that. Pick the same video again and your edits stay.',
      pick: 'Pick the video again',
      checking: 'Checking the video…',
      different: 'That looks like a different video. Pick the one you edited.',
    },
    errors: {
      failed: 'Couldn’t create the video: {{message}}',
      noEncoder: 'Your browser can’t encode video at this size. Pick a lower resolution.',
      empty: 'There’s nothing left to export.',
      storage: 'Not enough storage on this device.',
      stalled: 'The export stopped making progress. Try again; if it happens again, pick a lower quality.',
    },
  },
  record: {
    modesLabel: 'What to record',
    modes: { screen: 'Screen', 'screen-camera': 'Screen + camera', camera: 'Camera' },
    sources: 'Sound and camera',
    microphone: 'Microphone',
    camera: 'Camera',
    systemAudio: 'Tab and system sound',
    systemAudioHint: 'Tick “Share audio” in the next step',
    defaultDevice: 'Default',
    chooseMicrophone: 'Choose microphone',
    chooseCamera: 'Choose camera',
    micLevel: 'Microphone level',
    start: 'Start recording',
    choosing: 'Choose what to share…',
    trust: '{{size}} uploaded. Recorded on this device.',
    micSilent: 'Your microphone seems silent. Check it isn’t muted, or pick another one.',
    storageLow: 'About {{minutes}} min of space left on this device.',
    screenUnavailable: 'Screen recording needs a desktop browser. You can still film yourself.',
    cameraPreview: 'Your camera',
    microphoneProblem: {
      denied: 'Microphone access is blocked. Allow it from the icon in the address bar.',
      'not-found': 'No microphone was found.',
      'in-use': 'Another app is using the microphone.',
      other: 'The microphone couldn’t be started.',
    },
    cameraProblem: {
      denied: 'Camera access is blocked. Allow it from the icon in the address bar.',
      'not-found': 'No camera was found.',
      'in-use': 'Another app is using the camera.',
      other: 'The camera couldn’t be started.',
    },
    problems: {
      systemDenied: 'Your computer blocked screen recording. On a Mac, allow your browser in System Settings → Privacy & Security → Screen Recording, then try again.',
      screenUnsupported: 'This browser can’t record the screen. Use Chrome, Edge or Firefox on a computer.',
      screenFailed: 'The screen couldn’t be shared. Try again.',
      noEncoder: 'This browser can’t encode video at this size. Try sharing a window instead of the whole screen.',
      storageFull: 'There isn’t enough free space on this device to record.',
      prepareFailed: 'The recording couldn’t be prepared. It’s kept on this device; open it again from this page.',
      unknown: 'The recording stopped because of an unexpected problem.',
    },
    live: 'Recording',
    retake: 'Retake',
    retakeCountdown: 'Take {{take}} starts in',
    keepTake: 'Keep this take',
    takeLabel: 'Take {{take}}',
    notes: 'Speaker notes',
    notesHint: 'Read them while you record',
    notesLines_one: '{{count}} line, scrolls while recording',
    notesLines_other: '{{count}} lines, scroll while recording',
    notesPlaceholder: 'What you want to say, point by point. It scrolls by itself while you record and stops when you pause.',
    notesSpeed: 'Scroll speed',
    notesSpeeds: { slow: 'Slow', normal: 'Normal', fast: 'Fast' },
    notesShareHint: 'Share a window or a tab rather than the whole screen, so your notes stay out of the video.',
    starting: 'Starting…',
    savingShort: 'Saving…',
    paused: 'Paused',
    pause: 'Pause',
    resume: 'Resume',
    finish: 'Finish',
    discard: 'Discard',
    discardTitle: 'Discard this recording?',
    discardBody: 'It will be deleted from this device. This can’t be undone.',
    popOut: 'Float the controls',
    floating: 'The controls float above your other windows.',
    keepTab: 'Keep this tab open while recording. Space pauses, R retakes.',
    countdown: 'Recording starts in',
    preview: 'Live preview. The camera stays movable after recording.',
    saving: {
      user: 'Saving your recording…',
      'screen-ended': 'Sharing ended, so the recording stopped. Saving…',
      'storage-full': 'This device is almost out of space, so the recording stopped. Saving what was recorded…',
      failed: 'The recording stopped unexpectedly. Saving what was recorded…',
    },
    savingHint: 'Nothing is uploaded. This takes a few seconds.',
    documentTitle: { recording: '● {{time}} Recording', paused: 'Paused · {{time}}' },
  },
  projects: {
    title: 'Continue where you left off',
    meta: '{{clips}} · {{duration}} · {{when}}',
    clips_one: '{{count}} clip',
    clips_other: '{{count}} clips',
    continue: 'Continue',
    delete: 'Delete project',
    deleteTitle: 'Delete this project?',
    deleteBody: 'The edits to “{{name}}” will be deleted from this device. Your videos themselves aren’t touched.',
    deleteBodyRecording: 'The edits to “{{name}}” and its screen recording will be deleted from this device. This can’t be undone.',
    relink: {
      title: 'Pick the files again',
      body: 'Browsers let go of files once the page closes. You can select them all at once from their folder; Shycut works out which is which.',
      recordingGone: 'The screen recording is no longer on this device. Pick the recording itself and the edits fall back into place.',
      found: 'Found',
      missing: 'Waiting',
      pick_one: 'Pick the file',
      pick_other: 'Pick {{count}} files',
      matching: 'Matching…',
      open: 'Open project',
      strangers_one: '{{names}} isn’t part of this project.',
      strangers_other: 'These aren’t part of this project: {{names}}',
    },
  },
  transcript: {
    intro: 'Let’s write the speech down; then you edit the video like a text. Words you delete are cut from the video too. The AI runs on your device; the sound goes nowhere.',
    translated: 'These captions are an English translation of the {{language}} speech, so their words aren’t the ones said. To edit the video by its words, write them down as spoken.',
    writeAsSpoken: 'Write them in {{language}}',
    generate: 'Write the speech down',
    hint: 'Click a word to play from there. Select words and tap Cut (or press Delete): that part is cut from the video. Select struck-through words to bring them back.',
    label: 'Transcript',
    fillersHint_one: 'The {{count}} hesitation sound underlined with dots (um, uh) is cut.',
    fillersHint_other: 'The {{count}} hesitation sounds underlined with dots (um, uh) are cut.',
    idle: 'Select the words you want to cut.',
    selected_one: '{{count}} word · {{duration}}',
    selected_other: '{{count}} words · {{duration}}',
    cut: 'Cut',
    cutTitle: 'Cut from the video (Delete)',
    crutches: 'Crutch words',
    crutchesHint: 'They sometimes carry meaning, so they are never cut on their own. The script underlines them with dots.',
    times_one: '{{count}} time',
    times_other: '{{count}} times',
    cutAll: 'Cut all',
    restore: 'Bring back',
  },
  recovery: {
    title: 'A recording is still on this device',
    unfinishedHint: 'It was interrupted. Everything up to the last second was saved.',
    item: 'Recording from {{date}} · {{size}}',
    open: 'Open',
    delete: 'Delete',
    repairing: 'Repairing… {{percent}}%',
    deleteTitle: 'Delete this recording?',
    deleteBody: '{{item}} will be deleted from this device. This can’t be undone.',
  },
  chapters: {
    title: 'Chapters',
    hint: 'Chapters let viewers jump to a part, and YouTube shows them on the progress bar.',
    make: 'Make chapters',
    remake: 'Suggest again',
    none: 'There isn’t enough speech for chapters, or the video is shorter than a minute.',
    untitled: 'Chapter',
    titleLabel: 'Title of chapter {{number}}',
    remove: 'Remove chapter',
    cutAway: 'This chapter starts in a part that is cut.',
    tooFew: 'YouTube shows chapters when there are at least 3.',
    tooShort: 'YouTube needs every chapter to be at least 10 seconds long.',
    copy: 'Copy for YouTube',
    copied: 'Copied',
    outline: 'Download outline',
    outlineExplained: 'Downloads the chapter titles and times as a text file (Markdown), for a video description, a blog post or notes.',
    addHere: 'Add at playhead',
  },
  zoom: {
    title: 'Auto zoom',
    label: 'Zoom in on the action',
    hint: 'Follows where you type and click, and eases back out when the whole screen changes.',
    analyzing: 'Finding where you work… {{percent}}%',
    failed: 'The screen couldn’t be analysed for zooming.',
    amount: 'Strength',
    none: 'No steady spot to zoom into was found. The picture stays as it is.',
    moments_one: '{{count}} zoom moment. Turn one off if it isn’t needed.',
    moments_other: '{{count}} zoom moments. Turn one off if it isn’t needed.',
    skip: 'Turn this zoom off',
    include: 'Turn this zoom back on',
  },
  camera: {
    layout: 'Layout',
    layouts: {
      bubble: { label: 'Bubble', hint: 'Over the screen, in a corner' },
      split: { label: 'Split', hint: 'Camera and screen side by side' },
      off: { label: 'Hidden', hint: 'Screen only' },
    },
    background: 'Background',
    backgrounds: { none: 'As is', blur: 'Blur', color: 'Colour', image: 'Picture' },
    backgroundColor: 'Background colour',
    chooseImage: 'Choose a picture',
    changeImage: 'Change picture',
    imageFallback: 'Until you choose a picture, the room is blurred.',
    backgroundHint: 'Telling you apart from the room happens on this device.',
    splitSuggestion: 'For vertical video, a split frame shows you on top and the whole screen below.',
    useSplit: 'Use split',
    place: 'Position',
    corner: 'Corner',
    corners: { 'top-left': 'Top left', 'top-right': 'Top right', 'bottom-left': 'Bottom left', 'bottom-right': 'Bottom right' },
    dragHint: 'Or drag the bubble in the preview.',
    size: 'Size',
    shape: 'Shape',
    shapes: { circle: 'Circle', rounded: 'Rounded' },
    order: 'Order',
    orders: { cameraTop: 'Camera on top', screenTop: 'Screen on top', cameraLeft: 'Camera on the left', screenLeft: 'Screen on the left' },
    splitHint: 'The whole screen stays visible, so text stays readable.',
    offHint: 'The camera is hidden. It’s still here if you change your mind.',
  },
  media: {
    title: 'Media',
    import: 'Import',
    addText: 'Add text',
    show: 'Show media',
    hide: 'Hide media',
    resize: 'Width of the media list (drag, or use the arrow keys)',
    addToEnd: 'Add to the end',
    putOnTop: 'Put on top',
    addHere: 'Add at the playhead',
    remove: 'Remove from the list',
    inUse: 'In the video',
    picture: 'Picture',
    kinds: { video: 'Video', image: 'Picture', audio: 'Sound' },
    waiting_one: '{{count}} video is waiting to be added.',
    waiting_other: '{{count}} videos are waiting to be added.',
    addAll_one: 'Add it to the end',
    addAll_other: 'Add all to the end',
    empty: 'Add more videos to join them end to end, or a logo, picture or music to put on top. Everything stays on this device.',
    dragHint: 'Drag onto the timeline to drop it exactly where you want.',
    separately: 'Process the {{count}} videos one by one instead',
    separatelyTitle: 'Process them one by one?',
    separatelyBody: 'Each video gets your recipe on its own. The edits you made here don’t carry over.',
    separatelyConfirm: 'Process one by one',
    dropEnd: 'Add to the end',
    dropEndHint: 'Videos play after what you have now.',
    dropTop: 'Put on top',
    dropTopHint: 'Starts at the playhead: a logo, picture, music or another video.',
  },
  clips: {
    strip: 'Clips',
    choose: 'Clip {{index}}: {{name}}',
    position: 'Clip {{index}} of {{count}}',
    length: 'Length',
    fromVideo: 'From the video',
    arrange: 'Arrange',
    split: 'Split',
    splitTooltip: 'Splits the clip in two at the playhead. Nothing is removed; the pieces can be moved or taken out on their own.',
    splitHint: 'Splits the clip in two at the playhead, so each part can be moved or taken out.',
    splitWhere: 'To split it, move the playhead inside this clip.',
    splitEdge: 'Move the playhead to where the clip should be split; it can’t be split right at its edge.',
    goTo: 'Go to this clip',
    earlier: 'Earlier',
    later: 'Later',
    fit: 'Fit in the frame',
    fits: { contain: 'Show all of it', cover: 'Fill the frame' },
    fitHint: 'This clip has a different shape from your video: it either shows whole with bars, or its edges are cropped.',
    followHint: 'Cuts, captions and layers on this clip move with it.',
    transition: 'Between clips',
    transitions: { none: 'Cut', black: 'Dip to black', white: 'Dip to white' },
    transitionLength: 'Transition length',
    seconds: '{{value}} s',
    transitionHint: 'Applies wherever two clips meet. The sound carries on as it is.',
    remove: 'Take the clip out',
    onlyClip: 'The only clip of the video',
  },
  layers: {
    kinds: { video: 'Video on top', image: 'Picture', audio: 'Music or sound', text: 'Text' },
    list: 'Layers',
    actions: 'Layer actions',
    position: '{{index}} of {{count}}',
    hide: 'Hide',
    show: 'Show',
    hideName: 'Hide {{name}}',
    showName: 'Show {{name}}',
    lock: 'Lock',
    unlock: 'Unlock',
    lockName: 'Lock {{name}}',
    unlockName: 'Unlock {{name}}',
    duplicate: 'Duplicate',
    restack: 'Move {{name}} up or down',
    restackHint: 'Drag up or down to put it in front of or behind other layers (or use the arrow keys)',
    hiddenNotice: 'Hidden: it isn’t in the preview or the video. Its place and settings are kept.',
    lockedTiming: 'Locked: it can’t be dragged on the timeline. These buttons still move it.',
    lockedFrame: 'Locked: it can’t be dragged on the preview, and presses go through to what is under it.',
    placement: 'Placement',
    place: 'Place on the frame',
    anchors: {
      'top-left': 'Top left',
      'top-center': 'Top',
      'top-right': 'Top right',
      'middle-left': 'Left',
      'middle-center': 'Centre',
      'middle-right': 'Right',
      'bottom-left': 'Bottom left',
      'bottom-center': 'Bottom',
      'bottom-right': 'Bottom right',
    },
    text: {
      title: 'Text',
      default: 'Your text',
      words: 'Words',
      placeholder: 'Type here',
      hint: 'A new line starts with Enter.',
      look: 'Look',
      looks: { plain: 'Plain, with a soft shadow', outline: 'Outlined', box: 'On a dark band', tag: 'On a coloured tag' },
      color: 'Colour',
      colors: { ffffff: 'White', '0b0b0c': 'Black', c6f25e: 'Lime', ffe14d: 'Yellow', ff6b5e: 'Coral', '6cb8ff': 'Sky blue' },
      font: 'Font',
      fonts: { sans: 'Sans', serif: 'Serif', mono: 'Mono' },
      align: 'Alignment',
      aligns: { left: 'Left', center: 'Centre', right: 'Right' },
      bold: 'Bold',
      dragHint: 'Drag it on the preview to place it; pull its corner to resize it. Double-click it to type.',
    },
    bar: '{{kind}}: {{name}}, {{start}}–{{end}}',
    timing: 'Timing',
    startHere: 'Start here',
    endHere: 'End here',
    fromStart: 'From the beginning',
    wholeVideo: 'Whole video',
    timingHint: 'Drag it on the timeline to move it, or by an edge to make it longer or shorter.',
    look: 'Look',
    free: 'Picture in picture',
    fill: 'Full frame',
    size: 'Size',
    opacity: 'Opacity',
    dragHint: 'Drag it on the preview to place it; pull its corner to resize it.',
    resizeHint: 'Drag to resize',
    sound: 'Sound',
    volume: 'Volume',
    silent: 'Silent',
    duck: 'Lower while someone speaks',
    duckHint: 'Music gently drops while someone speaks in the video and comes back after.',
    fade: 'Fade in and out',
    motion: {
      title: 'Motion',
      in: 'Coming on',
      out: 'Going off',
      entrances: { none: 'None', fade: 'Fade', rise: 'Rise', pop: 'Pop', typewriter: 'Typewriter', words: 'Word by word' },
      exits: { none: 'None', fade: 'Fade', sink: 'Sink', pop: 'Shrink' },
      preview: 'Play it',
      hint: 'Shown the same in the preview and the saved video.',
    },
    loop: 'Start over when it ends',
    loopHint: 'Plays again from the start, so it can run longer.',
    facesNotHidden: 'Faces in videos you put on top aren’t hidden automatically yet.',
    forward: 'Bring forward',
    backward: 'Send backward',
    remove: 'Remove from the video',
  },
  speed: {
    title: 'Speed up',
    rate: 'Speed',
    start: 'Speed up from here',
    finish: 'End here',
    finishTitle: 'Speed up from {{start}} to here',
    marking: 'Move to where the faster part should end (play, or drag on the timeline), then press “End here”. Esc to cancel.',
    hint: 'Speed up a part such as a long walk or a quiet road: go to where it starts and press “Speed up from here”, then press “End here” where it should end. At {{limit}} and below the sound stays natural; faster parts are silent.',
    becomes: 'becomes {{duration}}',
    normal: 'Normal speed',
    clip: 'Clip speed',
    mixed: 'Parts of this clip play at different speeds. Pick one to play all of it at that speed.',
  },
  timeline: {
    mute: 'Turn the video’s sound off',
    unmute: 'Turn the video’s sound on',
    fit: 'Fit',
    fitHint: 'Show the whole video',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
  },
  selection: {
    label: 'Selected',
    close: 'Close',
  },
  shortcuts: {
    title: 'Keyboard shortcuts',
    lead: 'Press ? anywhere in the editor to see this again.',
    keys: { space: 'Space', delete: 'Delete' },
    groups: { playback: 'Playing', editing: 'Editing', layers: 'Layers', view: 'Timeline' },
    actions: {
      play: 'Play or pause',
      seek: 'Back or forward a second',
      seekFar: 'Back or forward 5 seconds',
      frame: 'One frame back or forward',
      trimIn: 'Start the video here',
      trimOut: 'End the video here',
      cut: 'Mark a part to remove, then remove it',
      split: 'Split the clip at the playhead',
      remove: 'Remove what is chosen',
      undo: 'Undo',
      redo: 'Redo',
      deselect: 'Let go of the mark or choice',
      addText: 'Add text at the playhead',
      duplicate: 'Duplicate the chosen layer',
      forward: 'Bring it forward',
      backward: 'Send it backward',
      hide: 'Hide or show it',
      lock: 'Lock or unlock it',
      nudge: 'Nudge it on the timeline',
      free: 'Hold while dragging to place freely',
      zoomIn: 'Zoom the timeline in',
      zoomOut: 'Zoom the timeline out',
      zoomFit: 'Fit the whole video',
      guide: 'Show these shortcuts',
    },
  },
};

export default en;
